// src/utils/sessionRecovery.js
// Automatic session recovery utilities for production use

import * as indexedDB from './indexedDB';

/**
 * Clear all authentication data from storage
 * Used when token is invalid or expired
 * @returns {Promise<void>}
 */
export const clearAuthData = async () => {
  //console.log('🧹 Clearing authentication data...');

  try {
    // Clear IndexedDB
    if (indexedDB.isAvailable()) {
      await indexedDB.removeItem('auth_token');
      await indexedDB.removeItem('refresh_token');
      await indexedDB.removeItem('user');
    }

    // Clear localStorage (fallback + ensure complete cleanup)
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');

    //console.log('✓ Auth data cleared successfully');
  } catch (error) {
    console.error('Error clearing auth data:', error);
    // Force localStorage cleanup even if IndexedDB fails
    try {
      localStorage.clear();
    } catch (e) {
      console.error('Critical: Unable to clear localStorage:', e);
    }
  }
};

/**
 * Handle session expiry gracefully
 * Clears data and redirects to login without user intervention
 * @param {string} reason - Why the session expired
 * @returns {Promise<void>}
 */
export const handleSessionExpiry = async (reason = 'session_expired') => {
  //console.log(`🔐 Session expired (${reason}), initiating automatic recovery...`);

  // Clear all auth data
  await clearAuthData();

  // Only redirect if not already on login/auth pages or public alarm pages
  const currentPath = window.location.pathname;
  const noRedirectPaths = ['/login', '/auth-callback', '/auth-error', '/alarm/', '/alarms/uuid/'];

  if (!noRedirectPaths.some(path => currentPath.startsWith(path))) {
    //console.log('➡️ Redirecting to login page...');
    // Preserve the current path for redirect after login
    const returnUrl = encodeURIComponent(currentPath + window.location.search);
    window.location.href = `/login?error=${reason}&return=${returnUrl}`;
  }
};

/**
 * Validate token health
 * Checks if token exists and is properly formatted
 * @returns {Promise<{valid: boolean, token: string|null, error: string|null}>}
 */
export const validateTokenHealth = async () => {
  try {
    let token = null;

    // Try IndexedDB first
    if (indexedDB.isAvailable()) {
      const encryptedToken = await indexedDB.getItem('auth_token');
      if (encryptedToken) {
        try {
          const { decryptToken } = await import('./tokenEncryption');
          token = await decryptToken(encryptedToken);
        } catch (decryptError) {
          return {
            valid: false,
            token: null,
            error: 'Token decryption failed - corrupted data'
          };
        }
      }
    }

    // Fallback to localStorage
    if (!token) {
      token = localStorage.getItem('auth_token');
    }

    if (!token) {
      return {
        valid: false,
        token: null,
        error: 'No token found'
      };
    }

    // Basic token format validation (JWT format: xxx.yyy.zzz)
    const isJWT = token.split('.').length === 3;
    if (!isJWT) {
      return {
        valid: false,
        token: null,
        error: 'Invalid token format'
      };
    }

    return {
      valid: true,
      token: token,
      error: null
    };
  } catch (error) {
    return {
      valid: false,
      token: null,
      error: error.message
    };
  }
};

/**
 * Attempt automatic session recovery
 * Tries to refresh the token before giving up
 * @returns {Promise<boolean>} - True if recovered, false otherwise
 */
export const attemptSessionRecovery = async () => {
  //console.log('🔄 Attempting automatic session recovery...');

  try {
    // Check if we have a refresh token
    let refreshToken = null;

    if (indexedDB.isAvailable()) {
      const encryptedRefreshToken = await indexedDB.getItem('refresh_token');
      if (encryptedRefreshToken) {
        const { decryptToken } = await import('./tokenEncryption');
        refreshToken = await decryptToken(encryptedRefreshToken);
      }
    } else {
      refreshToken = localStorage.getItem('refresh_token');
    }

    if (!refreshToken) {
      //console.log('❌ No refresh token available, cannot recover session');
      return false;
    }

    // Try to refresh the access token
    const { authApi } = await import('../services/apiClient');
    const response = await authApi.refreshToken(refreshToken);

    if (response.data.success && response.data.token) {
      //console.log('✓ Session recovered successfully!');
      return true;
    }

    return false;
  } catch (error) {
    console.error('❌ Session recovery failed:', error);
    return false;
  }
};

/**
 * Initialize session monitoring
 * Sets up automatic health checks and recovery
 */
export const initializeSessionMonitoring = () => {
  // Check session health periodically (every 5 minutes)
  const CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

  setInterval(async () => {
    const health = await validateTokenHealth();
    if (!health.valid && health.error !== 'No token found') {
      console.warn('⚠️ Token health check failed:', health.error);
      await handleSessionExpiry('token_corrupted');
    }
  }, CHECK_INTERVAL);

  //console.log('✓ Session monitoring initialized');
};

export default {
  clearAuthData,
  handleSessionExpiry,
  validateTokenHealth,
  attemptSessionRecovery,
  initializeSessionMonitoring
};
